import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import CategoryTree from '@/Pages/Catalog/CategoryTree';
import ItemsTab from '@/Pages/Catalog/ItemsTab';
import type { Category, Station } from '@/Pages/Catalog/lib';

/**
 * The catalog step — the Catalog screen's own items tab, in place.
 *
 * `ItemsTab` already takes the two lists it needs and an `onChanged`, so the
 * step is the real editor rather than a cut-down copy of it: the same product
 * form, the same variants, the same sold-out and bulk behaviour, the same
 * refusals. Loading `categories` and `stations` here is what `Catalog.tsx` does
 * for the same reason — the item form's category and station pickers are
 * populated from them.
 *
 * The category tree comes with it. `categoryId` is optional on
 * `CreateCatalogItemDto`, so an item *can* be created without one, but a
 * catalogue with no categories is a menu nobody can navigate — and sending
 * someone to another screen to make a category is exactly the round trip this
 * wizard exists to remove.
 */
export function ItemStep({ onWrote }: { onWrote: () => void }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [categoryId, setCategoryId] = useState('');

  const loadReference = useCallback(() => {
    api
      .get<{ data: Category[] }>('/categories', { params: { limit: 200 } })
      .then(({ data }) => setCategories(data.data ?? []))
      .catch(() => setCategories([]));
    // Stations need `kitchen.view`; without it the station picker is simply empty.
    api
      .get<Station[]>('/kitchen', { params: { limit: 100 } })
      .then(({ data }) => setStations(Array.isArray(data) ? data : []))
      .catch(() => setStations([]));
  }, []);

  useEffect(loadReference, [loadReference]);

  /**
   * A write landed. Refresh the pickers, then tell the wizard to re-probe —
   * which is the only thing that can tick this step. Nothing here decides the
   * step is done: a category created and an item that failed to save would
   * otherwise show a green tick over an empty catalogue.
   */
  const changed = () => {
    loadReference();
    onWrote();
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <CategoryTree
        className="lg:w-72 lg:shrink-0"
        selectedId={categoryId}
        onSelect={setCategoryId}
        onChanged={changed}
      />
      {/* `min-w-0` is what lets the item table scroll inside itself instead of
          pushing the tree off a tablet screen. */}
      <div className="min-w-0 flex-1">
        <ItemsTab
          categories={categories}
          stations={stations}
          onChanged={changed}
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
        />
      </div>
    </div>
  );
}
